import { OCR_TEMPLATE_PICKER_MAX } from '../constants';

/**
 * Pure selection rules for the Start OCR dialog's "Pick templates" tab,
 * bounded by `OCR_TEMPLATE_PICKER_MAX`.
 */

/**
 * Clicking a template: a selected one is removed; a new one is added, and
 * once the limit is reached the oldest pick makes room — so at a limit of 1
 * selecting another template replaces the current one.
 */
export function toggleTemplateSelection(
  selected: number[],
  templateId: number,
  max: number = OCR_TEMPLATE_PICKER_MAX
): number[] {
  if (selected.includes(templateId)) {
    return selected.filter((id) => id !== templateId);
  }
  return [...selected, templateId].slice(-max);
}

/**
 * Caps a set of template ids coming from elsewhere (a history run, an AI
 * suggestion list, a pool) to the limit, keeping the first — i.e. top-ranked —
 * ones.
 */
export function capTemplateSelection(
  templateIds: number[],
  max: number = OCR_TEMPLATE_PICKER_MAX
): number[] {
  return [...new Set(templateIds)].slice(0, max);
}

/**
 * Pre-selects a template (the container's default) without pushing out
 * anything the operator already picked: a full selection is left as-is.
 */
export function seedTemplateSelection(
  selected: number[],
  templateId: number,
  max: number = OCR_TEMPLATE_PICKER_MAX
): number[] {
  if (selected.includes(templateId)) return selected;
  return capTemplateSelection([...selected, templateId], max);
}

/**
 * Template ids of an AI suggestion list, best match first — the order
 * `capTemplateSelection` keeps from.
 */
export function rankedSuggestionIds(
  suggestions: ReadonlyArray<{ template_id: number; similarity: number }>
): number[] {
  return [...suggestions]
    .sort((a, b) => b.similarity - a.similarity)
    .map((entry) => entry.template_id);
}
